import React, { useEffect, useRef, useState } from "react";
import { PlayCircleFilled, PauseCircleFilled, SoundFilled, MutedFilled } from "@ant-design/icons";
import { Grid } from "antd";
import type { MediaEntry } from "./types";

const { useBreakpoint } = Grid;

// Drift correction threshold — matches the spirit (not the exact tuning) of dualcam's
// PlaybackActivity, which nudges the trailing player back in sync rather than freezing the UI.
const DRIFT_CORRECTION_SECONDS = 0.35;

/**
 * Front/back drift on a weak connection is handled here and *only* here — by nudging the trailing video
 * back to the leader while both play, plus `preload="auto"` on both panes so each buffers ahead.
 *
 * Do not reintroduce a "pause both while either is buffering" guard. Two attempts at one both made
 * playback worse, for different reasons: judging readiness from `readyState`/`buffered` deadlocked
 * playback outright (the front pane starts with an empty buffered range, which reads as a permanent
 * stall), and driving it from the browser's `waiting`/`stalled`/`canplay` events oscillated several
 * times a second, because pausing and resuming a video itself provokes those same events. Any control
 * loop that pauses in response to buffering ends up fighting the browser's own buffering. Drift while
 * one side stalls self-corrects below once both are playing again, which is the milder, stabler cure.
 */
const useVideoSync = (masterRef: React.RefObject<HTMLVideoElement | null>, slaveRef: React.RefObject<HTMLVideoElement | null>) => {
    useEffect(() => {
        const master = masterRef.current;
        const slave = slaveRef.current;
        if (!master || !slave) return;

        const play = () => { slave.currentTime = master.currentTime; slave.play().catch(() => {}); };
        const pause = () => slave.pause();

        // Seeking is where the two drift worst: the front has to fetch a fresh range at the new position
        // over the NAS link, and while it does the back — which may already have those bytes — keeps
        // playing, so the front comes back however long its seek took. Handled in two bounded steps
        // rather than by watching buffering: park the front, then realign it once it has actually landed.
        //
        // This is NOT the buffer guard that was removed (see useVideoSync's doc): it reacts only to an
        // explicit seek, listens to no `waiting`/`stalled` events, and ends at the front's own `seeked`,
        // so it can't oscillate. The timeout exists only so a seek that never completes can't leave the
        // front parked forever.
        let realignTimer: ReturnType<typeof setTimeout> | undefined;

        const seek = () => {
            slave.pause();
            slave.currentTime = master.currentTime;
            clearTimeout(realignTimer);
            realignTimer = setTimeout(finishSeek, 4000);
        };

        const finishSeek = () => {
            clearTimeout(realignTimer);
            // Realign to where the back is *now*, not where it was when the seek started — that gap is
            // exactly the desync. The second seek lands inside data the first one already buffered, so
            // it's instant rather than another round trip.
            if (Math.abs(slave.currentTime - master.currentTime) > 0.05) slave.currentTime = master.currentTime;
            if (!master.paused) slave.play().catch(() => {});
        };
        const checkDrift = () => {
            if (master.paused || slave.paused) return;
            if (Math.abs(slave.currentTime - master.currentTime) > DRIFT_CORRECTION_SECONDS) {
                slave.currentTime = master.currentTime;
            }
        };

        master.addEventListener("play", play);
        master.addEventListener("pause", pause);
        master.addEventListener("seeked", seek);
        master.addEventListener("timeupdate", checkDrift);
        // The front telling us it landed is what ends the parked state.
        slave.addEventListener("seeked", finishSeek);

        return () => {
            master.removeEventListener("play", play);
            master.removeEventListener("pause", pause);
            master.removeEventListener("seeked", seek);
            master.removeEventListener("timeupdate", checkDrift);
            slave.removeEventListener("seeked", finishSeek);
            clearTimeout(realignTimer);
        };
    }, [masterRef, slaveRef]);
};

const paneStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    background: "#000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};

const mediaStyle: React.CSSProperties = { width: "100%", height: "100%", maxHeight: "70vh", objectFit: "contain" };

// Width/height are best-effort (Synology's "resolution" field shape is unconfirmed, see
// SynologyShareClient.ParseItem) so this must degrade to the old fixed-height box when either is
// missing. When both are known, size the box by its real aspect ratio instead of forcing full pane
// width — otherwise a portrait phone video in a wide pane gets height-clamped to 70vh and
// letterboxed down to a sliver by objectFit:contain.
const mediaStyleFor = (entry: MediaEntry): React.CSSProperties =>
    entry.width && entry.height
        ? { ...mediaStyle, width: "auto", maxWidth: "100%", aspectRatio: `${entry.width} / ${entry.height}` }
        : mediaStyle;

const formatTime = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
};

const PairedVideoControls: React.FC<{
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    scrubTime: number | null;
    setScrubTime: (time: number) => void;
    commitScrub: () => void;
    togglePlay: () => void;
    muted: boolean;
    setMuted: React.Dispatch<React.SetStateAction<boolean>>;
    volume: number;
    setVolume: (volume: number) => void;
    isMobile: boolean;
}> = ({ isPlaying, currentTime, duration, scrubTime, setScrubTime, commitScrub, togglePlay, muted, setMuted, volume, setVolume, isMobile }) => {
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 8px", background: "#1f1f1f", borderRadius: 4 }}>
        <button type="button" aria-label={isPlaying ? "Pause" : "Play"} onClick={togglePlay} style={{ cursor: "pointer", fontSize: 22, display: "flex", color: "#fff", border: 0, padding: 0, background: "none" }}>
            {isPlaying ? <PauseCircleFilled /> : <PlayCircleFilled />}
        </button>
        <span style={{ fontSize: 12, minWidth: 36, textAlign: "right", color: "#fff" }}>{formatTime(scrubTime ?? currentTime)}</span>
        <input
            type="range"
            aria-label={isGerman ? "Videoposition" : "Video position"}
            min={0}
            max={duration || 0}
            step={0.1}
            value={Math.min(Number.isFinite(scrubTime ?? currentTime) ? scrubTime ?? currentTime : 0, Number.isFinite(duration) ? duration : 0)}
            onChange={e => setScrubTime(Number(e.target.value))}
            onPointerUp={commitScrub}
            onKeyUp={commitScrub}
            onBlur={commitScrub}
            style={{ flex: 1 }}
        />
        <span style={{ fontSize: 12, minWidth: 36, color: "#fff" }}>{formatTime(duration)}</span>
        <button type="button" aria-label={muted ? "Unmute" : "Mute"} onClick={() => setMuted(m => !m)} style={{ cursor: "pointer", fontSize: 18, display: "flex", color: "#fff", border: 0, padding: 0, background: "none" }}>
            {muted || volume === 0 ? <MutedFilled /> : <SoundFilled />}
        </button>
        {!isMobile && (
            <input
                type="range"
                aria-label="Volume"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                onChange={e => {
                    const next = Number(e.target.value);
                    setVolume(next);
                    setMuted(next === 0);
                }}
                style={{ width: 80, flexShrink: 0 }}
            />
        )}
    </div>
    );
};

const DualcamPlayer: React.FC<{ entry: MediaEntry }> = ({ entry }) => {
    const backRef = useRef<HTMLVideoElement>(null);
    const frontRef = useRef<HTMLVideoElement>(null);
    const isVideo = entry.mediaType === "video";
    const isPair = entry.kind === "pair" && entry.backUrl && entry.frontUrl;
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const [mediaFailedEntryId, setMediaFailedEntryId] = useState<string | null>(null);
    const mediaFailed = mediaFailedEntryId === entry.id;
    const [mediaRetry, setMediaRetry] = useState(0);
    useEffect(() => { setMediaFailedEntryId(null); setMediaRetry(0); }, [entry.id]);

    useVideoSync(backRef, frontRef);

    // False until the element's own `play` event says otherwise. Assuming autoplay succeeds is wrong
    // wherever a browser blocks it — no `pause` event fires for a blocked autoplay, so the bar would
    // sit showing Pause over two stopped videos and the first tap would look like it did nothing.
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    // Only the back pane carries audio (front is hard-muted), so one control drives the pair.
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    // Re-applied per entry as well as per change: stepping to the next item in the viewer swaps the
    // element's src, and a remount would otherwise silently reset it to full volume.
    useEffect(() => {
        const master = backRef.current;
        if (!master) return;
        master.volume = volume;
        master.muted = muted;
    }, [volume, muted, entry.id, isPair, isVideo]);

    // One shared control bar for the pair, driven off the back video (the sync hook above already
    // mirrors play/pause/seek onto front) — a native <video controls> on just one of the two panes
    // previously rendered a scrubber that only spanned that pane's half-width, which read as
    // "broken"/half-cut-off rather than as one timeline for the whole clip.
    useEffect(() => {
        if (!isPair || !isVideo) return;
        const master = backRef.current;
        if (!master) return;

        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onTime = () => setCurrentTime(master.currentTime);
        const onMeta = () => setDuration(master.duration || 0);

        master.addEventListener("play", onPlay);
        master.addEventListener("pause", onPause);
        master.addEventListener("timeupdate", onTime);
        master.addEventListener("loadedmetadata", onMeta);
        return () => {
            master.removeEventListener("play", onPlay);
            master.removeEventListener("pause", onPause);
            master.removeEventListener("timeupdate", onTime);
            master.removeEventListener("loadedmetadata", onMeta);
        };
    }, [isPair, isVideo]);

    const togglePlay = () => {
        const master = backRef.current;
        if (!master) return;
        if (master.paused) master.play().catch((error: unknown) => {
            const name = (error as { name?: string } | null)?.name;
            if (name !== "AbortError" && name !== "NotAllowedError") setMediaFailedEntryId(entry.id);
        }); else master.pause();
    };

    // While the handle is being dragged this holds the position under the finger; the actual seek waits
    // for release. A range input's onChange fires on every pixel of a drag, so seeking from it directly
    // fired a seek per pixel — each one a fresh range request to the NAS for *both* videos, which is what
    // made skipping desync so badly. Now one drag costs one seek.
    const [scrubTime, setScrubTime] = useState<number | null>(null);

    const seekTo = (time: number) => {
        const master = backRef.current;
        if (!master) return;
        master.currentTime = time;
        setCurrentTime(time);
    };

    const commitScrub = () => {
        if (scrubTime === null) return;
        seekTo(scrubTime);
        setScrubTime(null);
    };

    if (mediaFailed) return <div role="alert" style={{ ...paneStyle, color: "#fff", padding: 16, minHeight: 120 }}>{isGerman ? "Medien konnten nicht geladen werden." : "Media could not be loaded."} <button type="button" onClick={() => { setMediaFailedEntryId(null); setMediaRetry(value => value + 1); }}>{isGerman ? "Erneut versuchen" : "Retry"}</button></div>;

    if (!isPair) {
        const url = entry.backUrl ?? entry.frontUrl!;
        return (
            <div style={{ display: "flex", ...paneStyle }}>
                {isVideo ? (
                    <video key={`${entry.id}-${mediaRetry}`} src={url} controls autoPlay playsInline onError={() => setMediaFailedEntryId(entry.id)} style={mediaStyleFor(entry)} />
                ) : (
                    <img key={`${entry.id}-${mediaRetry}`} src={url} alt="" onError={() => setMediaFailedEntryId(entry.id)} style={mediaStyleFor(entry)} />
                )}
            </div>
        );
    }

    return (
        <div>
            <div key={mediaRetry} style={{ display: "flex", gap: 2, width: "100%" }}>
                {/* Tap-to-toggle lives on the pane, not the <video>. A portrait clip in a half-width
                    pane is sized by its own aspect ratio, so most of the pane is letterbox — on a phone
                    that letterbox is the majority of what a thumb actually lands on, and a handler on
                    the video element alone made tapping feel dead there while working fine on desktop.
                    playsInline is what stops iOS hijacking playback into its own fullscreen player,
                    where none of these handlers ever run. */}
                {isVideo ? (
                    <button type="button" aria-label="Toggle video playback" onClick={togglePlay} style={{ ...paneStyle, cursor: "pointer", border: 0, padding: 0 }}>
                        <video ref={backRef} src={entry.backUrl!} autoPlay playsInline preload="auto" onError={() => setMediaFailedEntryId(entry.id)} style={mediaStyleFor(entry)} />
                    </button>
                ) : (
                    <div style={paneStyle}><img src={entry.backUrl!} alt="Back" style={mediaStyleFor(entry)} /></div>
                )}
                {isVideo ? (
                    <button type="button" aria-label="Toggle video playback" onClick={togglePlay} style={{ ...paneStyle, cursor: "pointer", border: 0, padding: 0 }}>
                        <video ref={frontRef} src={entry.frontUrl!} muted playsInline preload="auto" onError={() => setMediaFailedEntryId(entry.id)} style={mediaStyleFor(entry)} />
                    </button>
                ) : (
                    <div style={paneStyle}><img src={entry.frontUrl!} alt="Front" style={mediaStyleFor(entry)} /></div>
                )}
            </div>

            {isVideo && <PairedVideoControls
                isPlaying={isPlaying}
                currentTime={currentTime}
                duration={duration}
                scrubTime={scrubTime}
                setScrubTime={setScrubTime}
                commitScrub={commitScrub}
                togglePlay={togglePlay}
                muted={muted}
                setMuted={setMuted}
                volume={volume}
                setVolume={setVolume}
                isMobile={isMobile}
            />}
        </div>
    );
};

export default DualcamPlayer;
